import { apiService } from './apiService.js';

/**
 * Conversion Service - Serviço para gerenciar conversões
 */
class ConversionService {

  /**
   * Criar nova conversão
   */
  async createConversion(conversionData) {
    try {
      const response = await apiService.post('/conversions', conversionData);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar conversão:', error);
      throw error;
    }
  }

  /**
   * Buscar conversão por ID
   */
  async getConversionById(id) {
    try {
      const response = await apiService.get(`/conversions/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar conversão:', error);
      throw error;
    }
  }

  /**
   * Buscar conversões com filtros e paginação
   */
  async getConversions(filters = {}, page = 1, limit = 20, orderBy = 'convertedAt', order = 'desc') {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        orderBy,
        order
      });

      if (Object.keys(filters).length > 0) {
        params.append('filters', JSON.stringify(filters));
      }

      const response = await apiService.get(`/conversions?${params}`);
      return {
        conversions: response.data,
        pagination: response.pagination,
        total: response.pagination?.total || 0
      };
    } catch (error) {
      console.error('Erro ao buscar conversões:', error);
      throw error;
    }
  }

  /**
   * Atualizar conversão
   */
  async updateConversion(id, updateData) {
    try {
      const response = await apiService.put(`/conversions/${id}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar conversão:', error);
      throw error;
    }
  }

  /**
   * Deletar conversão
   */
  async deleteConversion(id) {
    try {
      await apiService.delete(`/conversions/${id}`);
      return true;
    } catch (error) {
      console.error('Erro ao deletar conversão:', error);
      throw error;
    }
  }

  /**
   * Buscar conversões por lead
   */
  async getConversionsByLead(leadId) {
    try {
      const response = await apiService.get(`/conversions/lead/${leadId}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar conversões por lead:', error);
      throw error;
    }
  }

  /**
   * Atualizar status da conversão
   */
  async updateConversionStatus(id, status) {
    try {
      const response = await apiService.put(`/conversions/${id}/status`, { status });
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar status da conversão:', error);
      throw error;
    }
  }

  /**
   * Obter métricas de conversões
   */
  async getConversionMetrics(startDate = null, endDate = null) {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await apiService.get(`/conversions/metrics/overview?${params}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar métricas de conversões:', error);
      throw error;
    }
  }

  /**
   * Obter relatório de performance de vendas
   */
  async getSalesPerformance(startDate = null, endDate = null) {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await apiService.get(`/conversions/metrics/sales-performance?${params}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar performance de vendas:', error);
      throw error;
    }
  }

  // Utilitários para mapeamento de dados

  /**
   * Mapear tipo de conversão para exibição
   */
  getConversionTypeLabel(type) {
    const types = {
      'ECF_PREMIUM': 'ECF Premium',
      'ECF_BALANCED': 'ECF Balanced',
      'ECF_GROWTH': 'ECF Growth',
      'CUSTOM': 'Customizado'
    };
    return types[type] || type;
  }

  /**
   * Mapear status da conversão para exibição
   */
  getConversionStatusLabel(status) {
    const statuses = {
      'PENDING': 'Pendente',
      'STRUCTURED': 'Estruturada',
      'FUNDED': 'Financiada',
      'ACTIVE': 'Ativa',
      'COMPLETED': 'Concluída',
      'CANCELLED': 'Cancelada'
    };
    return statuses[status] || status;
  }

  /**
   * Obter cor do status para UI
   */
  getConversionStatusColor(status) {
    const colors = {
      'PENDING': 'bg-yellow-100 text-yellow-700',
      'STRUCTURED': 'bg-blue-100 text-blue-700',
      'FUNDED': 'bg-green-100 text-green-700',
      'ACTIVE': 'bg-green-100 text-green-700',
      'COMPLETED': 'bg-gray-100 text-gray-700',
      'CANCELLED': 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  }

  /**
   * Obter descrição do produto ECF
   */
  getProductDescription(type) {
    const descriptions = {
      'ECF_PREMIUM': '100% precatórios federais/estaduais - IRR 18-25%',
      'ECF_BALANCED': 'Carteira mista (40% precatórios, 35% trabalhistas, 25% cíveis) - IRR 25-32%',
      'ECF_GROWTH': 'Teses jurídicas emergentes - IRR 35-50%',
      'CUSTOM': 'Estrutura personalizada'
    };
    return descriptions[type] || 'Estrutura customizada';
  }

  /**
   * Calcular tempo de conversão
   */
  getConversionTime(leadCreatedAt, convertedAt) {
    if (!leadCreatedAt || !convertedAt) return 'N/A';

    const lead = new Date(leadCreatedAt);
    const conversion = new Date(convertedAt);
    const diffTime = Math.abs(conversion - lead);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return '1 dia';
    if (diffDays < 30) return `${diffDays} dias`;
    if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return months === 1 ? '1 mês' : `${months} meses`;
    }
    const years = Math.floor(diffDays / 365);
    return years === 1 ? '1 ano' : `${years} anos`;
  }

  /**
   * Formatar valor monetário
   */
  formatCurrency(value) {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  /**
   * Formatar percentual (IRR)
   */
  formatPercentage(value) {
    if (!value) return 'N/A';
    return `${value.toFixed(1)}%`;
  }

  /**
   * Calcular valor total de fee
   */
  getTotalFees(conversion) {
    const structuring = conversion.structuringFee || 0;
    const management = conversion.managementFee || 0;
    return structuring + management;
  }

  /**
   * Obter cor da performance IRR
   */
  getIrrPerformanceColor(irr) {
    if (!irr) return 'text-gray-500';
    if (irr >= 35) return 'text-green-600';
    if (irr >= 25) return 'text-yellow-600';
    if (irr >= 18) return 'text-blue-600';
    return 'text-red-600';
  }

  /**
   * Formatar composição da carteira
   */
  formatPortfolioComposition(composition) {
    if (!composition) return 'N/A';

    const parts = [];
    if (composition.precatorios) parts.push(`${composition.precatorios}% Precatórios`);
    if (composition.trabalhistas) parts.push(`${composition.trabalhistas}% Trabalhistas`);
    if (composition.civeis) parts.push(`${composition.civeis}% Cíveis`);
    if (composition.outros) parts.push(`${composition.outros}% Outros`);

    return parts.join(', ') || 'Composição não definida';
  }

  /**
   * Calcular ROI da conversão
   */
  calculateROI(conversion) {
    const totalFees = this.getTotalFees(conversion);
    const acquisitionCost = conversion.acquisitionCost || 0;
    const totalCost = acquisitionCost;

    if (totalCost === 0) return null;

    return ((totalFees - totalCost) / totalCost) * 100;
  }
}

export const conversionService = new ConversionService();